import React, { useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import {
    FiCheckCircle,
    FiClock,
    FiZap,
    FiPackage,
    FiUsers,
    FiCalendar,
    FiActivity,
} from 'react-icons/fi';

// ─── Datos del roadmap ───────────────────────────────────────────────────────

const ROADMAP_USER = [
  {
    version: 'roadmap.versionProximamente',
    estado: 'pendiente',
    fecha: null,
    items: [
        'roadmap.userPendienteExportar',
        'roadmap.userPendienteValoracion',
        'roadmap.userPendienteRecurrentes',
    ],
  },
  {
    version: 'v3.0',
    estado: 'publicada',
    fecha: 'roadmap.marzo2026',
    items: [
      'roadmap.userV2Correcciones',
    ],
  },
    {
    version: 'v1.3.0',
    estado: 'pasada',
    fecha: 'roadmap.marzo2026',
    items: [
      'roadmap.userV13Rendimiento',
      'roadmap.userV13Correcciones',
      'roadmap.userV13Telefono',
    ],
  },
];

const ROADMAP_INTERNO = [
  {
    version: 'v1.3.0',
    estado: 'publicada',
    fecha: 'roadmap.marzo2026',
    items: [
      'roadmap.internoGestion',
      'roadmap.internoCuentas',
      'roadmap.internoTickets',
      'roadmap.internoRoadmap',
    ],
  },
];

// ─── Badges por estado ───────────────────────────────────────────────────────

const ESTADO_META = {
  publicada:       { label: 'roadmap.estados.publicada',     cls: 'roadmap-badge--publicada',  icon: FiCheckCircle },
  'en-desarrollo': { label: 'roadmap.estados.desarrollo', cls: 'roadmap-badge--desarrollo', icon: FiClock },
  pendiente:       { label: 'roadmap.estados.pendiente',  cls: 'roadmap-badge--pendiente',  icon: FiZap },
  pasada:          null,
};

const ESTADO_ICONO = {
  publicada:       FiCheckCircle,
  'en-desarrollo': FiClock,
  pendiente:       FiZap,
  pasada:          FiPackage,
};

// ─── Leyenda de estados ──────────────────────────────────────────────────────

const LEYENDA = [
  { label: 'roadmap.estados.publicada', cls: 'roadmap-leyenda--publicada', icon: FiCheckCircle },
  { label: 'roadmap.estados.desarrollo', cls: 'roadmap-leyenda--desarrollo', icon: FiClock },
  { label: 'roadmap.estados.pendiente', cls: 'roadmap-leyenda--pendiente', icon: FiZap },
];

// ─── Componente lista de versiones con línea temporal ───────────────────────

const RoadmapLista = ({ data, t }) => (
  <div className="roadmap-timeline">
    {data.map((bloque, idx) => {
      const meta = ESTADO_META[bloque.estado] !== undefined ? ESTADO_META[bloque.estado] : null;
      const EstadoIcon = ESTADO_ICONO[bloque.estado] || FiPackage;
      const esUltimo = idx === data.length - 1;
      return (
        <div key={bloque.version} className={`roadmap-timeline-entrada${esUltimo ? ' roadmap-timeline-entrada--ultimo' : ''}`}>
          {/* Marcador lateral */}
          <div className="roadmap-timeline-lateral">
            <div className={`roadmap-timeline-nodo roadmap-timeline-nodo--${bloque.estado}`}>
              <EstadoIcon />
            </div>
          </div>

          {/* Tarjeta */}
          <div className={`roadmap-bloque roadmap-bloque--${bloque.estado}`}>
            <div className="roadmap-bloque-header">
              <div className="roadmap-bloque-header-izq">
                <span className={`roadmap-version-icon roadmap-version-icon--${bloque.estado}`}>
                  <EstadoIcon />
                </span>
                <span className="roadmap-version">{bloque.version.startsWith('roadmap.') ? t(bloque.version) : bloque.version}</span>
                {meta && <span className={`roadmap-badge ${meta.cls}`}>{t(meta.label)}</span>}
              </div>
              {bloque.fecha && (
                <span className="roadmap-fecha">
                  <FiCalendar />
                  {t(bloque.fecha)}
                </span>
              )}
            </div>
            <ul className="roadmap-items">
              {bloque.items.map((item, i) => (
                <li key={i} className="roadmap-item">
                  <span className="roadmap-item-dot" />
                  <span className="roadmap-item-text">{t(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    })}
  </div>
);

// ─── Componente principal ────────────────────────────────────────────────────

const Roadmap = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};
  const esInterno = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';

  const [tabActivo, setTabActivo] = useState('user');

  return (
    <main className="roadmap-page site-width">
      <div className="roadmap-cabecera">
        <div className="roadmap-cabecera-titulo">
          <span className="roadmap-cabecera-icon"><FiActivity /></span>
          <div>
            <h1 className="roadmap-titulo">{t('roadmap.titulo')}</h1>
            <p className="roadmap-subtitulo">
              {t('roadmap.subtitulo')}
            </p>
          </div>
        </div>
        <ul className="roadmap-leyenda">
          {LEYENDA.map((l) => {
            const Icon = l.icon;
            return (
              <li key={l.label} className={`roadmap-leyenda-item ${l.cls}`}>
                <span className="roadmap-leyenda-dot"><Icon /></span>
                <span>{t(l.label)}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {esInterno ? (
        <>
          <div className="roadmap-tabs">
            <button
              className={tabActivo === 'user' ? 'active' : ''}
              onClick={() => setTabActivo('user')}
            >
              <FiUsers />
              {t('roadmap.paraUsuarios')}
            </button>
            <button
              className={tabActivo === 'interno' ? 'active' : ''}
              onClick={() => setTabActivo('interno')}
            >
              <FiPackage />
              {t('roadmap.interno')}
            </button>
          </div>
          <div className="tabcontent roadmap-tabcontent">
            {tabActivo === 'user'    && <RoadmapLista data={ROADMAP_USER} t={t} />}
            {tabActivo === 'interno' && <RoadmapLista data={ROADMAP_INTERNO} t={t} />}
          </div>
        </>
      ) : (
        <RoadmapLista data={ROADMAP_USER} t={t} />
      )}
    </main>
  );
};

export default Roadmap;
